import { Routes } from '@angular/router';
import { adminShortInviteLinkMatcher } from './core/route-matchers/admin-short-invite.matcher';
import { AuthGuard } from './core/guards/auth.guard';
import { LoadingGuard } from './core/guards/loading.guard';
import { announcementEditCanDeactivateGuard } from './core/guards/announcement-edit.guard';
import { notClerkGuard } from './core/guards/not-clerk.guard';
import { managerOrAdminGuard } from './core/guards/manager-or-admin.guard';
import {
  appHomeRedirectGuard,
  organizationEmployeeChildGuard,
} from './core/guards/organization-employee.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'index',
    pathMatch: 'full',
  },
  // Auth action handler - MUST be before empty path route
  {
    path: 'auth/action',
    loadComponent: () =>
      import('./features/auth-action-handler/auth-action-handler.component').then(
        (m) => m.AuthActionHandlerComponent
      ),
  },
  // Auth link handler - Firebase auth magic links (fallback)
  {
    path: '__/auth/links',
    loadComponent: () =>
      import('./features/auth-link-handler/auth-link-handler.component').then(
        (m) => m.AuthLinkHandlerComponent
      ),
  },
  // Short ID handler - handles short IDs from deferredLinks function (like Flutter)
  // Use a specific path pattern to avoid conflicts with static assets
  {
    path: 'invite/:shortId',
    loadComponent: () =>
      import('./layout/landing-layout/landing-layout.component').then(
        (m) => m.LandingLayoutComponent
      ),
  },
  // User Registration - standalone page
  {
    path: 'user-registration',
    loadComponent: () =>
      import('./features/user-registration/user-registration-page/user-registration-page.component').then(
        (m) => m.UserRegistrationPageComponent
      ),
    data: {
      title: 'User Registration',
      description: 'Complete your user registration to get started.',
    },
  },
  // Business Registration - standalone page with landing layout
  {
    path: 'business-registration',
    loadComponent: () =>
      import('./features/business-registration/business-registration-page/business-registration-page.component').then(
        (m) => m.BusinessRegistrationPageComponent
      ),
    data: {
      title: 'Business Registration',
      description: 'Complete your business registration to access all features.',
    },
  },
  // Contact: standalone shell (not LandingLayoutComponent) — minimal header + same footer/brand as marketing
  {
    path: 'contact',
    loadComponent: () =>
      import('./layout/contact-landing-page/contact-landing-page.component').then(
        (m) => m.ContactLandingPageComponent
      ),
    data: {
      title: 'Contact',
      icon: 'contact_mail',
      description: 'Contact us for inquiries and support.',
    },
  },
  // Landing site pages with shared layout
  {
    path: '',
    loadComponent: () =>
      import('./layout/landing-layout/landing-layout.component').then(
        (m) => m.LandingLayoutComponent
      ),
    children: [
      {
        path: '',
        redirectTo: 'index-old',
        pathMatch: 'full',
      },
      {
        path: 'index-old',
        loadComponent: () =>
          import('./features/landing/landing.component').then((m) => m.LandingComponent),
        data: {
          title: 'Landing',
          icon: 'home',
          description: 'Main landing page for the service platform.',
        },
      },
      {
        path: 'about',
        loadComponent: () =>
          import('./features/about/about.component').then((m) => m.AboutComponent),
        data: {
          title: 'About',
          icon: 'info',
          description: 'Learn more about our company and mission.',
        },
      },
      {
        path: 'service',
        loadComponent: () =>
          import('./features/service/service.component').then((m) => m.ServiceComponent),
        data: {
          title: 'Services',
          icon: 'build',
          description: 'Our comprehensive service offerings.',
        },
      },
    ],
  },
  // Static agreements - standalone without layout
  {
    path: 'agreements/:userType/:fileName',
    loadComponent: () =>
      import('./features/agreements/agreements.component').then((m) => m.AgreementsComponent),
    data: {
      title: 'Agreements',
      description: 'Legal agreements and terms.',
    },
  },
  {
    path: 'agreements/:fileName',
    loadComponent: () =>
      import('./features/agreements/agreements.component').then((m) => m.AgreementsComponent),
    data: {
      title: 'Agreements',
      description: 'Legal agreements and terms.',
    },
  },
  {
    path: 'index',
    loadComponent: () =>
      import('./features/property-managers-page/property-managers-page').then(
        (m) => m.PropertyManagersPage
      ),
    data: {
      title: 'Property Managers',
      description: 'Modern SaaS Landing Page for Property Managers',
    },
  },
  {
    path: 'property-managers',
    redirectTo: 'index',
    pathMatch: 'full',
  },
  // Main app routes with layout
  {
    path: 'app',
    loadComponent: () => import('./layout/main-layout/main-layout').then((m) => m.MainLayout),
    canActivateChild: [organizationEmployeeChildGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [appHomeRedirectGuard],
        loadComponent: () =>
          import('./layout/empty-route/empty-route.component').then(
            (m) => m.EmptyRouteComponent
          ),
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./features/dashboard/dashboard').then((m) => m.Dashboard),
        canActivate: [AuthGuard],
      },
      {
        path: 'requests',
        loadComponent: () =>
          import('./features/requests/requests-list/requests-list.component').then(
            (m) => m.RequestsListComponent
          ),
        canActivate: [AuthGuard],
        data: {
          title: 'Requests',
          icon: 'assignment',
          description: 'Manage service requests and track their status.',
        },
      },
      {
        path: 'tasks',
        loadComponent: () =>
          import('./features/requests/requests-list/requests-list.component').then(
            (m) => m.RequestsListComponent
          ),
        canActivate: [AuthGuard, notClerkGuard],
        data: {
          title: 'Tasks',
          icon: 'task',
          description: 'Manage internal maintenance tasks.',
          requestType: 'task',
          listTitleKey: 'tasks.title',
          breadcrumbsKey: 'breadcrumbs.tasks',
        },
      },
      {
        path: 'requests/:id',
        loadComponent: () =>
          import('./features/requests/request-details/request-details.component').then(
            (m) => m.RequestDetailsComponent
          ),
        canActivate: [AuthGuard, LoadingGuard],
      },
      {
        path: 'requests/:id/chat',
        loadComponent: () =>
          import('./features/requests/request-chat/request-chat.component').then(
            (m) => m.RequestChatComponent
          ),
        canActivate: [AuthGuard, LoadingGuard],
      },
      {
        path: 'employees',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/employees/employees-list.component').then(
                (m) => m.EmployeesListComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Employees',
              icon: 'people',
              description: 'Manage employees and roles.',
            },
          },
          {
            path: 'add',
            loadComponent: () =>
              import('./features/employees/employee-detail/employee-detail.component').then(
                (m) => m.EmployeeDetailComponent
              ),
            canActivate: [AuthGuard],
            data: { title: 'Add Employee' },
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/employees/employee-detail/employee-detail.component').then(
                (m) => m.EmployeeDetailComponent
              ),
            canActivate: [AuthGuard],
            data: { title: 'Employee' },
          },
        ],
      },
      {
        path: 'properties',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/properties/properties.component').then((m) => m.PropertiesComponent),
            data: {
              title: 'Properties',
              icon: 'home_work',
              description: 'Manage property information and details.',
            },
          },
          {
            path: 'add',
            loadComponent: () =>
              import('./features/properties/add-property/add-property.component').then(
                (m) => m.AddPropertyComponent
              ),
            data: {
              title: 'Add Property',
            },
          },
          {
            path: 'details/:id',
            loadComponent: () =>
              import('./features/properties/property-details/property-details.component').then(
                (m) => m.PropertyDetailsComponent
              ),
            data: {
              title: 'Property Details',
            },
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import('./features/properties/add-property/add-property.component').then(
                (m) => m.AddPropertyComponent
              ),
            data: {
              title: 'Edit Property',
            },
          },
          {
            path: 'add-resident',
            loadComponent: () =>
              import('./features/properties/add-resident/add-resident.component').then(
                (m) => m.AddResidentComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Add Resident',
            },
          },
          {
            path: 'import-residents',
            loadComponent: () =>
              import('./features/properties/import-residents/import-residents.component').then(
                (m) => m.ImportResidentsComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Import Residents',
            },
          },
          {
            path: 'units/:propertyId',
            loadComponent: () =>
              import('./features/properties/units/units.component').then(
                (m) => m.UnitsComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Units',
            },
          },
          {
            path: 'units/:propertyId/add-unit',
            loadComponent: () =>
              import('./features/properties/units/add-unit/add-unit.component').then(
                (m) => m.AddUnitComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Add Unit',
            },
          },
          {
            path: 'units/:propertyId/add-unit/:unitId',
            loadComponent: () =>
              import('./features/properties/units/add-unit/add-unit.component').then(
                (m) => m.AddUnitComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Edit Unit',
            },
          },
          {
            path: 'units/:propertyId/add-tenant',
            loadComponent: () =>
              import('./features/properties/units/add-tenant/add-tenant.component').then(
                (m) => m.AddTenantComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Add Tenant',
            },
          },
          {
            path: 'units/:propertyId/add-tenant/:unitId',
            loadComponent: () =>
              import('./features/properties/units/add-tenant/add-tenant.component').then(
                (m) => m.AddTenantComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Add Tenant',
            },
          },
          {
            path: 'units/:propertyId/add-tenant/:unitId/:affiliationId',
            loadComponent: () =>
              import('./features/properties/units/add-tenant/add-tenant.component').then(
                (m) => m.AddTenantComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Edit Tenant',
            },
          },
          {
            path: 'units/:propertyId/add-owner',
            loadComponent: () =>
              import('./features/properties/units/add-owner/add-owner.component').then(
                (m) => m.AddOwnerComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Add Owner',
            },
          },
          {
            path: 'units/:propertyId/add-owner/:unitId',
            loadComponent: () =>
              import('./features/properties/units/add-owner/add-owner.component').then(
                (m) => m.AddOwnerComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Add Owner',
            },
          },
          {
            path: 'units/:propertyId/add-owner/:unitId/:affiliationId',
            loadComponent: () =>
              import('./features/properties/units/add-owner/add-owner.component').then(
                (m) => m.AddOwnerComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Edit Owner',
            },
          },
          {
            path: 'units/:propertyId/add-lease',
            loadComponent: () =>
              import('./features/properties/units/add-lease/add-lease.component').then(
                (m) => m.AddLeaseComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Add Lease',
            },
          },
          {
            path: 'units/:propertyId/add-lease/:unitId',
            loadComponent: () =>
              import('./features/properties/units/add-lease/add-lease.component').then(
                (m) => m.AddLeaseComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Add Lease',
            },
          },
          {
            path: 'units/:propertyId/add-lease/:unitId/:leaseId',
            loadComponent: () =>
              import('./features/properties/units/add-lease/add-lease.component').then(
                (m) => m.AddLeaseComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Edit Lease',
            },
          },
          {
            path: 'units/:propertyId/details/:unitId',
            loadComponent: () =>
              import('./features/properties/units/unit-details/unit-details.component').then(
                (m) => m.UnitDetailsComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Unit Details',
            },
          },
          {
            path: 'units/:propertyId/:unitId/profiles/:type',
            loadComponent: () =>
              import('./features/properties/units/affiliations/affiliations.component').then(
                (m) => m.AffiliationsComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Profiles',
            },
          },
          {
            path: 'units/:propertyId/add-applicant/:unitId',
            loadComponent: () =>
              import('./features/properties/units/add-applicant/add-applicant.component').then(
                (m) => m.AddApplicantComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Add Applicant',
            },
          },
          {
            path: 'units/:propertyId/add-applicant/:unitId/:affiliationId',
            loadComponent: () =>
              import('./features/properties/units/add-applicant/add-applicant.component').then(
                (m) => m.AddApplicantComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Edit Applicant',
            },
          },
          {
            path: 'parking/:propertyId',
            loadComponent: () =>
              import('./features/properties/parking/parking.component').then(
                (m) => m.ParkingComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Parking',
            },
          },
          {
            path: 'parking/:propertyId/add-parking',
            loadComponent: () =>
              import('./features/properties/parking/add-parking/add-parking.component').then(
                (m) => m.AddParkingComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Add Parking',
            },
          },
          {
            path: 'parking/:propertyId/add-parking/:parkingId',
            loadComponent: () =>
              import('./features/properties/parking/add-parking/add-parking.component').then(
                (m) => m.AddParkingComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Edit Parking',
            },
          },
          {
            path: 'lockers/:propertyId',
            loadComponent: () =>
              import('./features/properties/lockers/locker.component').then(
                (m) => m.LockerComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Lockers',
            },
          },
          {
            path: 'lockers/:propertyId/add-locker',
            loadComponent: () =>
              import('./features/properties/lockers/add-locker/add-locker.component').then(
                (m) => m.AddLockerComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Add Locker',
            },
          },
          {
            path: 'lockers/:propertyId/add-locker/:lockerId',
            loadComponent: () =>
              import('./features/properties/lockers/add-locker/add-locker.component').then(
                (m) => m.AddLockerComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Edit Locker',
            },
          },
        ],
      },
      {
        path: 'amenities',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/amenities/amenities-list.component').then(
                (m) => m.AmenitiesListComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Amenities',
              icon: 'meeting_room',
              description: 'Manage property amenities and calendars.',
            },
          },
          {
            path: 'new',
            loadComponent: () =>
              import('./features/amenities/amenity-edit.component').then(
                (m) => m.AmenityEditComponent
              ),
            canActivate: [AuthGuard],
            data: { title: 'New Amenity' },
          },
          {
            path: ':propertyId/:id',
            loadComponent: () =>
              import('./features/amenities/amenity-edit.component').then(
                (m) => m.AmenityEditComponent
              ),
            canActivate: [AuthGuard],
            data: { title: 'Amenity' },
          },
        ],
      },
      {
        path: 'residents',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/residents/residents.component').then((m) => m.ResidentsComponent),
            canActivate: [AuthGuard],
            data: {
              title: 'Tenants',
              icon: 'people',
              description: 'Manage tenant accounts and information.',
            },
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import('./features/properties/add-resident/add-resident.component').then(
                (m) => m.AddResidentComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Edit Tenant',
            },
          },
        ],
      },
      {
        path: 'applicants',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/applicants/applicants.component').then((m) => m.ApplicantsComponent),
            canActivate: [AuthGuard],
            data: {
              title: 'Applicants',
              icon: 'people',
              description: 'Manage applicant profiles.',
            },
          },
          {
            path: 'add',
            loadComponent: () =>
              import('./features/applicants/add-applicant-page/add-applicant-page.component').then(
                (m) => m.AddApplicantPageComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Add Applicant',
            },
          },
          {
            path: 'edit/:profileId',
            loadComponent: () =>
              import('./features/applicants/add-applicant-page/add-applicant-page.component').then(
                (m) => m.AddApplicantPageComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Edit Applicant',
            },
          },
        ],
      },
      {
        path: 'vendors',
        loadComponent: () =>
          import('./features/vendors/vendors.component').then((m) => m.VendorsComponent),
        canActivate: [AuthGuard, notClerkGuard],
        data: {
          title: 'Vendors',
          icon: 'local_shipping',
          description: 'Manage vendor accounts and services.',
        },
      },
      {
        path: 'invoices',
        canActivate: [AuthGuard, notClerkGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/invoices/invoices.component').then((m) => m.InvoicesComponent),
            data: {
              title: 'Invoices',
              icon: 'receipt',
              description: 'Manage invoices and payment tracking.',
            },
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/invoices/invoice-detail/invoice-detail.component').then((m) => m.InvoiceDetailComponent),
            data: {
              title: 'Invoice Details',
              icon: 'receipt',
              description: 'View invoice details and PDF.',
            },
          },
        ],
      },
      {
        path: 'calendar',
        loadComponent: () =>
          import('./features/placeholder/placeholder').then((m) => m.Placeholder),
        canActivate: [AuthGuard],
        data: {
          title: 'Calendar',
          icon: 'calendar_today',
          description: 'View and manage events and schedules.',
        },
      },
      {
        path: 'announcements',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/announcements/announcements-list.component').then(
                (m) => m.AnnouncementsListComponent
              ),
            canActivate: [AuthGuard],
            data: {
              title: 'Announcements',
              icon: 'campaign',
              description: 'Create and manage community announcements.',
            },
          },
          {
            path: 'new',
            loadComponent: () =>
              import('./features/announcements/announcement-edit.component').then(
                (m) => m.AnnouncementEditComponent
              ),
            canActivate: [AuthGuard],
            canDeactivate: [announcementEditCanDeactivateGuard],
            data: { title: 'New Announcement', newAnnouncement: true },
          },
          {
            path: ':propertyId/:id',
            loadComponent: () =>
              import('./features/announcements/announcement-edit.component').then(
                (m) => m.AnnouncementEditComponent
              ),
            canActivate: [AuthGuard],
            canDeactivate: [announcementEditCanDeactivateGuard],
            data: { title: 'Announcement' },
          },
        ],
      },
      {
        path: 'classified-ads',
        canActivate: [AuthGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/classified-ads/classified-ads-list.component').then(
                (m) => m.ClassifiedAdsListComponent
              ),
            data: {
              title: 'Classified Ads',
              icon: 'article',
              description: 'Manage classified advertisements and listings.',
            },
          },
          {
            path: ':listingId',
            loadComponent: () =>
              import('./features/classified-ads/listing-detail.component').then(
                (m) => m.ListingDetailComponent
              ),
            data: { title: 'Listing' },
          },
        ],
      },
      {
        path: 'amenity-bookings',
        loadComponent: () =>
          import('./features/placeholder/placeholder').then((m) => m.Placeholder),
        canActivate: [AuthGuard],
        data: {
          title: 'Amenity Bookings',
          icon: 'king_bed',
          description: 'Manage amenity reservations and bookings.',
        },
      },
      {
        path: 'pet-registries',
        loadComponent: () =>
          import('./features/placeholder/placeholder').then((m) => m.Placeholder),
        canActivate: [AuthGuard],
        data: {
          title: 'Pet Registries',
          icon: 'pets',
          description: 'Manage pet registrations and information.',
        },
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./features/placeholder/placeholder').then((m) => m.Placeholder),
        canActivate: [AuthGuard],
        data: {
          title: 'Notifications',
          icon: 'notifications',
          description: 'View and manage system notifications.',
        },
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/placeholder/placeholder').then((m) => m.Placeholder),
        data: {
          title: 'Settings',
          icon: 'settings',
          description: 'Configure system settings and preferences.',
        },
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/profile/profile.component').then((m) => m.ProfileComponent),
        canActivate: [AuthGuard],
        data: {
          title: 'Profile',
          icon: 'person',
          description: 'Manage your user profile and account settings.',
        },
      },
      {
        path: 'support',
        loadComponent: () =>
          import('./features/support/support.component').then((m) => m.SupportComponent),
        canActivate: [AuthGuard],
        data: { title: 'Support', icon: 'help', description: 'Get help and contact support.' },
      },
      {
        path: 'about',
        loadComponent: () =>
          import('./features/about-app/about-app.component').then((m) => m.AboutAppComponent),
        canActivate: [AuthGuard],
        data: {
          title: 'About',
          icon: 'info',
          description: 'Learn more about the Service Admin application.',
        },
      },
      {
        path: 'business',
        loadComponent: () =>
          import('./features/business-info/business-info.component').then(
            (m) => m.BusinessInfoComponent
          ),
        canActivate: [AuthGuard, notClerkGuard],
        data: {
          title: 'Business',
          icon: 'business',
          description: 'View and manage your business information.',
        },
      },
      {
        path: 'business/edit',
        loadComponent: () =>
          import('./features/business-info/edit-business-info/edit-business-info.component').then(
            (m) => m.EditBusinessInfoComponent
          ),
        canActivate: [AuthGuard, notClerkGuard],
        data: {
          title: 'Edit Business',
          description: 'Edit your business information.',
        },
      },
      {
        path: 'business/smtp',
        loadComponent: () =>
          import('./features/business-info/smtp-settings/smtp-settings.component').then(
            (m) => m.SmtpSettingsComponent
          ),
        canActivate: [AuthGuard, notClerkGuard],
        data: {
          title: 'SMTP Settings',
          description: 'Configure business SMTP credentials.',
        },
      },
      {
        path: 'service-request',
        canActivate: [AuthGuard, notClerkGuard],
        children: [
          {
            path: '',
            redirectTo: 'choose-service',
            pathMatch: 'full',
          },
          {
            path: 'select-vendor',
            loadComponent: () =>
              import(
                './features/service-request/select-vendor-for-request/select-vendor-for-request.component'
              ).then((m) => m.SelectVendorForRequestComponent),
          },
          {
            path: 'choose-service',
            loadComponent: () =>
              import('./features/service-request/choose-service/choose-service').then(
                (m) => m.ChooseService
              ),
          },
          {
            path: 'choose-provider/:serviceId',
            loadComponent: () =>
              import('./features/service-request/choose-provider/choose-provider').then(
                (m) => m.ChooseProvider
              ),
          },
          {
            path: 'provider-details/:serviceId/:providerId',
            loadComponent: () =>
              import('./features/service-request/provider-details/provider-details').then(
                (m) => m.ProviderDetails
              ),
          },
        ],
      },
      {
        path: 'request-wizard',
        loadComponent: () =>
          import('./features/request/request-wizard/request-wizard.component').then(
            (m) => m.RequestWizardComponent
          ),
      },
      {
        path: 'task-wizard/:requestId',
        canActivate: [AuthGuard, managerOrAdminGuard],
        loadComponent: () =>
          import('./features/task/task-wizard/task-wizard.component').then(
            (m) => m.TaskWizardComponent
          ),
      },
      {
        path: 'task-wizard',
        canActivate: [AuthGuard, managerOrAdminGuard],
        loadComponent: () =>
          import('./features/task/task-wizard/task-wizard.component').then(
            (m) => m.TaskWizardComponent
          ),
      },
    ],
  },
  // Bare short link on admin origin (e.g. devlink 302 misconfigured → SPA serves /AbCdEf).
  // Must be before ** so we do not redirect to /index and lose the shortId.
  {
    matcher: adminShortInviteLinkMatcher,
    loadComponent: () =>
      import('./layout/landing-layout/landing-layout.component').then(
        (m) => m.LandingLayoutComponent
      ),
  },
  // Wildcard route - MUST BE LAST
  // Catches all unmatched routes and redirects to landing (404 handler)
  // Note: This will only fire after APP_INITIALIZER completes, so auth state
  // is always determined before this route is evaluated
  {
    path: '**',
    redirectTo: '/index',
  },
];
